import { useEffect, useMemo, useState, type ReactNode } from "react"
import type { GuidebookData, GuidebookHouseRules, GuidebookPlace, PlaceCategory } from "../types"
import { PLACE_CATEGORIES, getCategoryForType } from "../types"
import { getVoiceCopy } from "../../house-voice-copy"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  Check,
  ChevronRight,
  Clock3,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  Info,
  Lock,
  MapPin,
  Search,
  ShieldCheck,
  Sparkles,
  Wifi,
  KeyRound,
  type LucideIcon,
} from "lucide-react"
import {
  NAV_ICON_MAP,
  getHighlightIcon,
  getHouseRuleIcon,
  getPlaceIcon,
  getPropertyInfoIcon,
  type HouseRuleKey,
  type NavKey,
  type PropertyInfoKey,
} from "./iconMap"

interface Props {
  data: GuidebookData
  slug?: string
  guestToken?: string
  onRefresh?: () => void
  showUnlockPrompt?: boolean
  onOpenUnlock?: () => void
}

type NavTab = NavKey
type ExploreFilter = "All" | PlaceCategory
type AccessState = "public" | "authenticated"
type DoorCodeVisibility = "available_now" | "available_on_arrival" | "locked" | "not_configured"

interface RuleGroup {
  key: HouseRuleKey
  title: string
  items: string[]
}

interface PropertyInfoItem {
  key: PropertyInfoKey
  label: string
  value: string
}

interface HighlightCardData {
  slot: GuidebookData["highlights"] extends Array<infer T> ? T extends { slot: infer S } ? S : never : never
  label: string
  motivation: string
  place: GuidebookPlace
}

const FILTER_ICON_TYPE: Record<PlaceCategory, string> = {
  "Eat & drink": "restaurant",
  "Coffee & bakery": "cafe",
  Beach: "beach",
  Activities: "attraction",
  Shopping: "shop",
  Other: "generic",
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return false
    return window.matchMedia("(min-width: 900px)").matches
  })

  useEffect(() => {
    if (typeof window === "undefined") return undefined
    const mediaQuery = window.matchMedia("(min-width: 900px)")
    const listener = (event: MediaQueryListEvent) => setIsDesktop(event.matches)
    setIsDesktop(mediaQuery.matches)
    mediaQuery.addEventListener("change", listener)
    return () => mediaQuery.removeEventListener("change", listener)
  }, [])

  return isDesktop
}

function copyToClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(value)
  }

  const fallback = document.createElement("textarea")
  fallback.value = value
  document.body.appendChild(fallback)
  fallback.select()
  document.execCommand("copy")
  document.body.removeChild(fallback)
  return Promise.resolve()
}

function GlassCard({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <Card
      className={cn(
        "relative overflow-hidden rounded-[1.75rem] border border-border/60 bg-card/72 shadow-[0_18px_60px_rgba(24,22,15,0.08)] backdrop-blur-xl",
        className,
      )}
    >
      {children}
    </Card>
  )
}

function SectionLabel({ icon: Icon, label, description, action }: { icon: LucideIcon; label: string; description?: string; action?: ReactNode }) {
  return (
    <div className="flex items-end justify-between gap-4">
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-[0.72rem] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
          <span>{label}</span>
        </div>
        {description && <p className="max-w-[44rem] text-sm leading-6 text-muted-foreground">{description}</p>}
      </div>
      {action}
    </div>
  )
}

function SectionEmpty({ icon: Icon, title, message }: { icon: LucideIcon; title: string; message: string }) {
  return (
    <GlassCard className="p-6 sm:p-7">
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-border/60 bg-muted text-foreground">
          <Icon className="h-5 w-5" />
        </div>
        <div className="space-y-1">
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          <p className="text-sm leading-6 text-muted-foreground">{message}</p>
        </div>
      </div>
    </GlassCard>
  )
}

function SectionError({ title, message }: { title: string; message: string }) {
  return (
    <GlassCard className="border-destructive/20 bg-destructive/5 p-6 sm:p-7">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-destructive/20 bg-card/80 text-foreground">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div className="space-y-1">
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          <p className="text-sm leading-6 text-muted-foreground">{message}</p>
        </div>
      </div>
    </GlassCard>
  )
}

function EssentialsSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {[0, 1].map((index) => (
        <GlassCard key={index} className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <Skeleton className="h-12 w-12 rounded-2xl" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-3.5 w-20" />
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="h-10 w-10 rounded-full" />
          </div>
        </GlassCard>
      ))}
    </div>
  )
}

function TimelineSkeleton() {
  return (
    <GlassCard className="p-5 sm:p-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className="space-y-3 rounded-[1.35rem] border border-border/50 bg-background/55 p-4">
            <Skeleton className="h-10 w-10 rounded-2xl" />
            <Skeleton className="h-3.5 w-16" />
            <Skeleton className="h-5 w-24" />
          </div>
        ))}
      </div>
    </GlassCard>
  )
}

function HighlightsSkeleton() {
  return (
    <div className="flex gap-4 overflow-x-auto pb-2">
      {[0, 1].map((index) => (
        <GlassCard key={index} className="min-w-[18rem] flex-1 p-5 sm:min-w-[22rem] sm:p-6">
          <div className="space-y-4">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-6 w-44" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        </GlassCard>
      ))}
    </div>
  )
}

function PlacesSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} className="h-10 w-28 rounded-full" />
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map((index) => (
          <GlassCard key={index} className="p-5 sm:p-6">
            <div className="space-y-4">
              <Skeleton className="h-12 w-12 rounded-2xl" />
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          </GlassCard>
        ))}
      </div>
    </div>
  )
}

function CredentialCard({
  icon: Icon,
  label,
  value,
  secret,
  secretVisible,
  canToggle,
  onToggle,
  onCopy,
  copied,
  hint,
}: {
  icon: LucideIcon
  label: string
  value: string
  secret?: string
  secretVisible?: boolean
  canToggle?: boolean
  onToggle?: () => void
  onCopy?: () => void
  copied?: boolean
  hint?: string
}) {
  return (
    <GlassCard className="p-5 sm:p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-border/60 bg-muted text-foreground shadow-sm">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{label}</p>
          <p className="truncate text-lg font-semibold text-foreground">{value}</p>
          {secret && (
            <p className="font-mono text-sm tracking-[0.18em] text-muted-foreground">
              {secretVisible ? secret : "••••••••"}
            </p>
          )}
          {hint && <p className="text-sm leading-6 text-muted-foreground">{hint}</p>}
        </div>
        {(canToggle || onCopy) && (
          <div className="flex shrink-0 items-center gap-2">
            {canToggle && onToggle && (
              <button
                type="button"
                onClick={onToggle}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-border/60 bg-background/70 text-foreground transition hover:border-border hover:bg-muted"
                aria-label={secretVisible ? `Hide ${label}` : `Reveal ${label}`}
              >
                {secretVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            )}
            {onCopy && (
              <button
                type="button"
                onClick={onCopy}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-border/60 bg-background/70 text-foreground transition hover:border-border hover:bg-muted"
                aria-label={`Copy ${label}`}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            )}
          </div>
        )}
      </div>
    </GlassCard>
  )
}

function HomePanel({
  data,
  accessState,
  doorCodeVisibility,
  doorCodePolicyText,
  showUnlockPrompt,
  onOpenUnlock,
  onExplore,
  onSelectPlace,
}: {
  data: GuidebookData
  accessState: AccessState
  doorCodeVisibility: DoorCodeVisibility
  doorCodePolicyText?: string
  showUnlockPrompt?: boolean
  onOpenUnlock?: () => void
  onExplore: () => void
  onSelectPlace: (place: GuidebookPlace) => void
}) {
  const [wifiVisible, setWifiVisible] = useState(false)
  const [doorVisible, setDoorVisible] = useState(false)
  const [wifiCopied, setWifiCopied] = useState(false)
  const [doorCopied, setDoorCopied] = useState(false)
  const isAuthenticated = accessState === "authenticated"

  const timelineItems = useMemo(() => buildTimelineItems(data), [data])
  const highlightCards = useMemo(() => buildHighlightCards(data), [data])
  const welcomeCopy = data.welcome?.hero_message || data.welcome?.message || data.property.region_description
  const heroSummary = getHeroSummary(data)
  const hasEssentials = Boolean(data.property.wifi_name || doorCodeVisibility !== "not_configured")
  const lockCopy = doorCodePolicyText || "Verify your booking to reveal private arrival details."

  async function handleCopy(value: string, setter: (next: boolean) => void) {
    await copyToClipboard(value)
    setter(true)
    window.setTimeout(() => setter(false), 1800)
  }

  return (
    <div className="space-y-6 sm:space-y-7">
      <HeroCard
        data={data}
        summary={heroSummary}
        welcomeCopy={welcomeCopy}
        locked={Boolean(showUnlockPrompt && onOpenUnlock)}
        onPrimaryAction={showUnlockPrompt && onOpenUnlock ? onOpenUnlock : onExplore}
      />

      <section className="space-y-4">
        <SectionLabel
          icon={KeyRound}
          label="Stay essentials"
          description="WiFi, entry access, and the details guests reach for first."
        />

        {showUnlockPrompt && onOpenUnlock && (
          <GlassCard className="p-5 sm:p-6">
            <div className="absolute bottom-5 left-0 top-5 w-[3px] rounded-full bg-primary" />
            <div className="flex flex-col gap-5 pl-4 sm:flex-row sm:items-center sm:justify-between sm:pl-5">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-border/60 bg-muted text-foreground">
                  <Lock className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-foreground">Stay details protected</h3>
                  <p className="max-w-[42rem] text-sm leading-6 text-muted-foreground">{lockCopy}</p>
                </div>
              </div>
              <Button
                type="button"
                onClick={onOpenUnlock}
                className="rounded-full bg-primary px-5 text-primary-foreground hover:bg-primary/90"
              >
                Unlock stay details
              </Button>
            </div>
          </GlassCard>
        )}

        {!hasEssentials ? (
          <SectionEmpty
            icon={Info}
            title="No essentials published yet"
            message="Add arrival details in the property settings to show WiFi and access information here."
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {data.property.wifi_name && (
              <CredentialCard
                icon={Wifi}
                label="WiFi"
                value={data.property.wifi_name}
                secret={data.property.wifi_password || undefined}
                secretVisible={wifiVisible}
                canToggle={Boolean(isAuthenticated && data.property.wifi_password)}
                onToggle={() => setWifiVisible((current) => !current)}
                onCopy={isAuthenticated && data.property.wifi_password
                  ? () => void handleCopy(data.property.wifi_password, setWifiCopied)
                  : undefined}
                copied={wifiCopied}
                hint={!isAuthenticated && data.property.wifi_password ? "Password available after booking verification." : undefined}
              />
            )}

            {doorCodeVisibility !== "not_configured" && (
              <CredentialCard
                icon={KeyRound}
                label="Door code"
                value={getDoorCodeLabel(data, accessState, doorCodeVisibility, doorVisible)}
                secret={isAuthenticated && doorCodeVisibility === "available_now" && data.property.door_code ? data.property.door_code : undefined}
                secretVisible={doorVisible}
                canToggle={Boolean(isAuthenticated && doorCodeVisibility === "available_now" && data.property.door_code)}
                onToggle={() => setDoorVisible((current) => !current)}
                onCopy={isAuthenticated && doorCodeVisibility === "available_now" && data.property.door_code
                  ? () => void handleCopy(data.property.door_code, setDoorCopied)
                  : undefined}
                copied={doorCopied}
                hint={getDoorCodeHint(accessState, doorCodeVisibility, doorCodePolicyText)}
              />
            )}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <SectionLabel
          icon={CalendarDays}
          label="Stay timeline"
          description="Arrival windows and departure context for the current booking."
        />
        {timelineItems.length === 0 ? (
          <SectionEmpty
            icon={CalendarDays}
            title="Stay dates unavailable"
            message="Check-in and check-out moments appear here as soon as Stayframe has booking context for this guest."
          />
        ) : (
          <GlassCard className="p-5 sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {timelineItems.map(({ icon: Icon, label, value, note }) => (
                <div key={label} className="rounded-[1.35rem] border border-border/50 bg-background/55 p-4 shadow-sm">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl border border-border/60 bg-muted text-foreground">
                    <Icon className="h-4.5 w-4.5" />
                  </div>
                  <p className="text-[0.72rem] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{label}</p>
                  <p className="mt-1 text-lg font-semibold text-foreground">{value}</p>
                  {note && <p className="mt-1 text-sm leading-6 text-muted-foreground">{note}</p>}
                </div>
              ))}
            </div>
          </GlassCard>
        )}
      </section>

      <section className="space-y-4">
        <SectionLabel
          icon={Sparkles}
          label="Concierge picks"
          description="AI-selected places with context on why they fit this stay right now."
        />
        {highlightCards.length === 0 ? (
          <SectionEmpty
            icon={Sparkles}
            title="No picks ready yet"
            message="When highlighted recommendations are available, they will appear here with a reason for this stay."
          />
        ) : (
          <div className="flex snap-x gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {highlightCards.map(({ slot, label, motivation, place }) => {
              const Icon = getHighlightIcon(slot)
              return (
                <button
                  key={`${slot}-${place.id}`}
                  type="button"
                  onClick={() => onSelectPlace(place)}
                  className="group min-w-[18.5rem] snap-start text-left sm:min-w-[23rem]"
                >
                  <GlassCard className="h-full p-5 transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_22px_60px_rgba(24,22,15,0.1)] sm:p-6">
                    <div className="absolute bottom-5 left-0 top-5 w-[3px] rounded-full bg-primary" />
                    <div className="space-y-4 pl-4 sm:pl-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                          <Icon className="h-5 w-5" />
                        </div>
                        <Badge variant="outline" className="rounded-full border-border/70 bg-background/70 px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                          {label}
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-display text-[1.9rem] font-normal leading-none text-foreground">
                          {place.en?.name || place.id}
                        </h3>
                        <p className="text-sm leading-6 text-foreground">{motivation}</p>
                      </div>
                      <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                        <span>{place.area || formatTypeLabel(place.type) || "Nearby"}</span>
                        <span className="inline-flex items-center gap-1 text-foreground transition group-hover:translate-x-0.5">
                          View details
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </div>
                  </GlassCard>
                </button>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}

function ExplorePanel({
  places,
  highlights,
  selectedFilter,
  onFilterChange,
  onSelectPlace,
}: {
  places: GuidebookPlace[]
  highlights: GuidebookPlace[]
  selectedFilter: ExploreFilter
  onFilterChange: (filter: ExploreFilter) => void
  onSelectPlace: (place: GuidebookPlace) => void
}) {
  const filteredPlaces = useMemo(
    () => places.filter((place) => selectedFilter === "All" || getCategoryForType(place.type || "") === selectedFilter),
    [places, selectedFilter],
  )

  return (
    <div className="space-y-6 sm:space-y-7">
      <section className="space-y-4">
        <SectionLabel
          icon={Search}
          label="Explore nearby"
          description="Filter nearby recommendations by moment, mood, or the kind of plan your guests want to make." 
        />

        <div className="flex gap-2 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <FilterPill
            active={selectedFilter === "All"}
            label="All places"
            onClick={() => onFilterChange("All")}
            icon={MapPin}
          />
          {PLACE_CATEGORIES.map((category) => (
            <FilterPill
              key={category}
              active={selectedFilter === category}
              label={category}
              onClick={() => onFilterChange(category)}
              icon={getPlaceIcon(FILTER_ICON_TYPE[category])}
            />
          ))}
        </div>
      </section>

      {highlights.length > 0 && (
        <section className="space-y-4">
          <SectionLabel
            icon={Sparkles}
            label="Featured now"
            description="AI highlights surfaced in a separate row so the best-fit options never get lost in the full list."
          />
          <div className="grid gap-4 min-[900px]:grid-cols-2">
            {highlights.slice(0, 2).map((place) => (
              <button key={place.id} type="button" onClick={() => onSelectPlace(place)} className="text-left">
                <GlassCard className="p-5 transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_22px_60px_rgba(24,22,15,0.1)] sm:p-6">
                  <div className="absolute bottom-5 left-0 top-5 w-[3px] rounded-full bg-primary" />
                  <div className="flex items-start gap-4 pl-4 sm:pl-5">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      {(() => {
                        const Icon = getPlaceIcon(place.type)
                        return <Icon className="h-5 w-5" />
                      })()}
                    </div>
                    <div className="min-w-0 space-y-2">
                      <p className="font-display text-[1.7rem] font-normal leading-none text-foreground">{place.en?.name || place.id}</p>
                      <p className="text-sm leading-6 text-muted-foreground">{place.en?.why || place.en?.short || "A recommended stop for this stay."}</p>
                    </div>
                  </div>
                </GlassCard>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-4">
        {filteredPlaces.length === 0 ? (
          <SectionEmpty
            icon={Search}
            title="No places in this filter"
            message="Try another category to explore the rest of the guidebook recommendations."
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filteredPlaces.map((place) => (
              <button
                key={place.id}
                type="button"
                onClick={() => onSelectPlace(place)}
                className="text-left"
              >
                <GlassCard className="h-full p-5 transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_22px_60px_rgba(24,22,15,0.1)] sm:p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-border/60 bg-muted text-foreground">
                      {(() => {
                        const Icon = getPlaceIcon(place.type)
                        return <Icon className="h-5 w-5" />
                      })()}
                    </div>
                    <div className="min-w-0 flex-1 space-y-3">
                      <div className="space-y-1">
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="text-base font-semibold text-foreground">{place.en?.name || place.id}</h3>
                          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {[formatTypeLabel(place.type), place.area].filter(Boolean).join(" · ") || "Local recommendation"}
                        </p>
                      </div>
                      {(place.en?.short || place.en?.why) && (
                        <p className="text-sm leading-6 text-foreground">{place.en?.short || place.en?.why}</p>
                      )}
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        {place.priceRange && (
                          <Badge variant="outline" className="rounded-full border-border/70 bg-background/70 px-3 py-1 text-[0.68rem] uppercase tracking-[0.18em] text-muted-foreground">
                            {place.priceRange}
                          </Badge>
                        )}
                        {place.openingHours?.summary && (
                          <Badge variant="outline" className="rounded-full border-border/70 bg-background/70 px-3 py-1 text-[0.68rem] uppercase tracking-[0.18em] text-muted-foreground">
                            Hours
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function GuidePanel({ data }: { data: GuidebookData }) {
  const ruleGroups = useMemo(() => buildRuleGroups(data.houseRules, data.property), [data.houseRules, data.property])
  const propertyInfo = useMemo(() => buildPropertyInfo(data), [data])

  return (
    <div className="space-y-6 sm:space-y-7">
      <section className="space-y-4">
        <SectionLabel
          icon={Info}
          label="About the area"
          description="Context for the surrounding neighbourhood, region, and how this property sits within it."
        />
        {data.property.region_description ? (
          <GlassCard className="p-6 sm:p-7">
            <div className="absolute bottom-6 left-0 top-6 w-[3px] rounded-full bg-primary" />
            <div className="space-y-3 pl-4 sm:pl-5">
              <h2 className="font-display text-[2rem] font-normal leading-tight text-foreground">A local brief</h2>
              <p className="max-w-[70ch] text-sm leading-7 text-foreground">{data.property.region_description}</p>
            </div>
          </GlassCard>
        ) : (
          <SectionEmpty
            icon={Info}
            title="Area notes coming soon"
            message="Add a region description in property settings to give guests context beyond the address."
          />
        )}
      </section>

      <section className="space-y-4">
        <SectionLabel
          icon={ShieldCheck}
          label="House rules"
          description="Grouped essentials so expectations stay clear without feeling heavy-handed."
        />
        {ruleGroups.length === 0 ? (
          <SectionEmpty
            icon={ShieldCheck}
            title="No house rules published"
            message="Enable guidebook-facing rules to populate this section for guests."
          />
        ) : (
          <div className="grid gap-4 min-[900px]:grid-cols-2">
            {ruleGroups.map((group) => {
              const Icon = getHouseRuleIcon(group.key)
              return (
                <GlassCard key={group.key} className="p-5 sm:p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-border/60 bg-muted text-foreground">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="space-y-3">
                      <div>
                        <h3 className="text-base font-semibold text-foreground">{group.title}</h3>
                        <p className="text-sm leading-6 text-muted-foreground">{group.items[0]}</p>
                      </div>
                      {group.items.length > 1 && (
                        <div className="space-y-2">
                          {group.items.slice(1).map((item) => (
                            <div key={item} className="flex items-start gap-2 text-sm leading-6 text-muted-foreground">
                              <span className="mt-[0.45rem] h-1.5 w-1.5 shrink-0 rounded-full bg-border-strong" />
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </GlassCard>
              )
            })}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <SectionLabel
          icon={MapPin}
          label="Property info"
          description="Practical on-property details that support the stay beyond arrival credentials."
        />
        {propertyInfo.length === 0 ? (
          <SectionEmpty
            icon={MapPin}
            title="No property notes added"
            message="Parking, waste, and local errand tips will appear here when they are configured."
          />
        ) : (
          <div className="grid gap-4 min-[640px]:grid-cols-2 xl:grid-cols-3">
            {propertyInfo.map((item) => {
              const Icon = getPropertyInfoIcon(item.key)
              return (
                <GlassCard key={item.key} className="p-5 sm:p-6">
                  <div className="space-y-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-border/60 bg-muted text-foreground">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-base font-semibold text-foreground">{item.label}</h3>
                      <p className="text-sm leading-6 text-muted-foreground">{item.value}</p>
                    </div>
                  </div>
                </GlassCard>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}

function HeroCard({
  data,
  summary,
  welcomeCopy,
  locked,
  onPrimaryAction,
}: {
  data: GuidebookData
  summary?: string | null
  welcomeCopy?: string | null
  locked: boolean
  onPrimaryAction: () => void
}) {
  const accessLabel = data.is_public ? "Guest access required" : "Stay unlocked"

  return (
    <GlassCard className="min-h-[24rem] p-6 sm:p-7 lg:p-8">
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage: data.property.cover_image_url
            ? `linear-gradient(135deg, rgba(251,250,249,0.14), rgba(255,255,255,0.74)), url(${data.property.cover_image_url})`
            : "radial-gradient(circle at top left, color-mix(in srgb, var(--sf-primary) 14%, transparent), transparent 42%), linear-gradient(135deg, rgba(255,255,255,0.86), rgba(255,255,255,0.62))",
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(251,250,249,0.42),rgba(251,250,249,0.86))]" />

      <div className="relative grid gap-6 min-[900px]:grid-cols-[minmax(0,1.2fr)_minmax(20rem,0.8fr)] min-[900px]:items-end">
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2.5">
            <Badge variant="outline" className="rounded-full border-border/70 bg-card/78 px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground backdrop-blur-sm">
              Reserve
            </Badge>
            <Badge variant="outline" className="rounded-full border-border/70 bg-card/78 px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground backdrop-blur-sm">
              {accessLabel}
            </Badge>
            {summary && (
              <Badge variant="outline" className="rounded-full border-border/70 bg-card/78 px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground backdrop-blur-sm">
                {summary}
              </Badge>
            )}
          </div>

          <div className="space-y-4">
            <div className="space-y-3">
              <h1 className="max-w-[12ch] font-display text-[clamp(2.9rem,9vw,5.5rem)] font-normal leading-[0.92] tracking-[-0.03em] text-foreground">
                {data.property.name}
              </h1>
              {data.property.address && (
                <div className="flex items-start gap-2 text-sm leading-6 text-muted-foreground">
                  <MapPin className="mt-1 h-4 w-4 shrink-0" />
                  <span>{data.property.address}</span>
                </div>
              )}
            </div>

            <div className="max-w-[42rem] text-base leading-7 text-foreground sm:text-lg">
              {welcomeCopy || "A guest guide shaped around the stay, ready when guests need the next decision made."}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              onClick={onPrimaryAction}
              variant={locked ? "default" : "outline"}
              className={cn(
                "rounded-full px-5",
                locked
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "border-border/70 bg-card/78 text-foreground hover:bg-card/90",
              )}
            >
              {locked ? "Unlock stay details" : "Explore nearby"}
            </Button>
          </div>
        </div>

        <GlassCard className="border-white/40 bg-card/58 p-5 shadow-[0_12px_40px_rgba(24,22,15,0.08)] backdrop-blur-2xl sm:p-6">
          <div className="absolute bottom-5 left-0 top-5 w-[3px] rounded-full bg-primary" />
          <div className="space-y-3 pl-4 sm:pl-5">
            <div className="text-[0.72rem] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Editorial note
            </div>
            <p className="font-display text-[1.8rem] font-normal leading-tight text-foreground">
              {data.welcome?.headline || data.welcome?.welcome_headline || "A calm, premium mobile concierge."}
            </p>
            <p className="text-sm leading-7 text-muted-foreground">
              {data.welcome?.message || data.welcome?.welcome_message || "Warm neutrals, practical guidance, and just enough context to help a guest move with confidence."}
            </p>
          </div>
        </GlassCard>
      </div>
    </GlassCard>
  )
}

function FilterPill({ icon: Icon, label, active, onClick }: { icon: LucideIcon; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition",
        active
          ? "border-primary/20 bg-card/95 text-foreground shadow-sm"
          : "border-border/60 bg-card/75 text-foreground hover:border-border hover:bg-card",
      )}
    >
      <Icon className={cn("h-4 w-4", active && "text-primary")} />
      <span>{label}</span>
    </button>
  )
}

function PlaceDetailSheet({ place, open, onOpenChange, isDesktop }: { place: GuidebookPlace | null; open: boolean; onOpenChange: (open: boolean) => void; isDesktop: boolean }) {
  if (!place) return null

  const Icon = getPlaceIcon(place.type)
  const tags = [place.priceRange, place.area, place.familyFriendly ? "Family friendly" : null].filter(Boolean) as string[]
  const sections = [
    { title: "Overview", value: place.en?.short },
    { title: "Why it fits", value: place.en?.why },
    { title: "Tip", value: place.en?.tip },
    { title: "Rainy day alternative", value: place.en?.rainAlternative },
    { title: "Best for", value: place.en?.bestFor },
  ].filter((section) => section.value)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isDesktop ? "right" : "bottom"}
        className={cn(
          "border-border/70 bg-background/96 p-0 shadow-2xl backdrop-blur-xl",
          isDesktop
            ? "w-full min-[900px]:max-w-[31rem]"
            : "rounded-t-[1.75rem]",
        )}
      >
        <div className="flex h-full flex-col overflow-hidden">
          <SheetHeader className="border-b border-border/60 bg-card/72 px-6 pb-5 pt-8 text-left shadow-sm backdrop-blur-sm sm:px-7">
            <div className="flex items-start gap-4 pr-8">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-border/60 bg-muted text-foreground">
                <Icon className="h-5 w-5" />
              </div>
              <div className="space-y-2">
                <SheetTitle className="font-display text-[2rem] font-normal leading-none text-foreground">
                  {place.en?.name || place.id}
                </SheetTitle>
                <SheetDescription className="text-sm leading-6 text-muted-foreground">
                  {[formatTypeLabel(place.type), place.area].filter(Boolean).join(" · ") || "Local recommendation"}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6 sm:px-7">
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="rounded-full border-border/70 bg-card/70 px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            {place.openingHours?.summary && (
              <GlassCard className="p-5">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-border/60 bg-muted text-foreground">
                    <Clock3 className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[0.72rem] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Opening hours</p>
                    <p className="mt-1 text-sm leading-6 text-foreground">{place.openingHours.summary}</p>
                  </div>
                </div>
              </GlassCard>
            )}

            {sections.map((section) => (
              <div key={section.title} className="space-y-2">
                <h3 className="text-[0.72rem] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{section.title}</h3>
                <p className="text-sm leading-7 text-foreground">{section.value}</p>
              </div>
            ))}

            {Array.isArray(place.tags) && place.tags.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-[0.72rem] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {place.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="rounded-full border-border/70 bg-card/70 px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {(place.website || place.coordinates) && (
            <div className="border-t border-border/60 bg-card/72 px-6 py-4 backdrop-blur-sm sm:px-7">
              <div className="flex flex-wrap gap-3">
                {place.website && (
                  <Button asChild variant="outline" className="rounded-full border-border/70 bg-background/80 px-4">
                    <a href={place.website} target="_blank" rel="noopener noreferrer">
                      Website
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                )}
                {place.coordinates && (
                  <Button asChild className="rounded-full bg-primary px-4 text-primary-foreground hover:bg-primary/90">
                    <a
                      href={`https://maps.google.com/?q=${place.coordinates.lat},${place.coordinates.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Get directions
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

function DesktopNav({ activeTab, onChange }: { activeTab: NavTab; onChange: (tab: NavTab) => void }) {
  return (
    <div className="sticky top-4 z-20 hidden min-[900px]:block">
      <GlassCard className="border-white/50 bg-card/78 px-3 py-3 backdrop-blur-2xl">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[0.68rem] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Guest guide</div>
            <div className="text-sm text-foreground">Reserve theme</div>
          </div>
          <div className="flex items-center gap-2">
            {(Object.keys(NAV_ICON_MAP) as NavTab[]).map((tab) => {
              const Icon = NAV_ICON_MAP[tab]
              const active = activeTab === tab
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => onChange(tab)}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition",
                    active
                      ? "border border-primary/20 bg-card text-foreground shadow-sm"
                      : "text-foreground hover:bg-muted",
                  )}
                >
                  <Icon className={cn("h-4 w-4", active && "text-primary")} />
                  <span className="capitalize">{tab}</span>
                </button>
              )
            })}
          </div>
        </div>
      </GlassCard>
    </div>
  )
}

function MobileNav({ activeTab, onChange }: { activeTab: NavTab; onChange: (tab: NavTab) => void }) {
  return (
    <div className="fixed inset-x-4 bottom-4 z-30 min-[900px]:hidden">
      <GlassCard className="border-white/50 bg-card/82 px-2 py-2 backdrop-blur-2xl">
        <div className="grid grid-cols-3 gap-1">
          {(Object.keys(NAV_ICON_MAP) as NavTab[]).map((tab) => {
            const Icon = NAV_ICON_MAP[tab]
            const active = activeTab === tab
            return (
              <button
                key={tab}
                type="button"
                onClick={() => onChange(tab)}
                className={cn(
                  "flex flex-col items-center gap-1 rounded-[1.25rem] px-3 py-2.5 text-[0.75rem] font-medium transition",
                  active ? "border border-primary/20 bg-card text-foreground shadow-sm" : "text-foreground",
                )}
              >
                <Icon className={cn("h-4.5 w-4.5", active && "text-primary")} />
                <span className="capitalize">{tab}</span>
              </button>
            )
          })}
        </div>
      </GlassCard>
    </div>
  )
}

export function ReserveShell({ data, showUnlockPrompt, onOpenUnlock }: Props) {
  const [activeTab, setActiveTab] = useState<NavTab>("home")
  const [selectedFilter, setSelectedFilter] = useState<ExploreFilter>("All")
  const [selectedPlace, setSelectedPlace] = useState<GuidebookPlace | null>(null)
  const isDesktop = useIsDesktop()

  const accessState: AccessState = data.is_public ? "public" : "authenticated"
  const doorCodeVisibility = deriveDoorCodeVisibility(data)
  const highlightedPlaces = useMemo(() => {
    const highlightIds = new Set((data.highlights || []).map((highlight) => highlight.place_id))
    return data.places.filter((place) => highlightIds.has(place.id))
  }, [data.highlights, data.places])

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0"
        style={{
          background:
            "radial-gradient(circle at top, color-mix(in srgb, var(--sf-primary) 10%, transparent), transparent 40%), radial-gradient(circle at bottom right, rgba(24,22,15,0.04), transparent 28%), linear-gradient(180deg, rgba(251,250,249,1), rgba(251,250,249,0.96))",
        }}
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-[1120px] flex-col px-4 pb-28 pt-4 sm:px-6 sm:pb-32 min-[900px]:px-8 min-[900px]:pb-12">
        <DesktopNav activeTab={activeTab} onChange={setActiveTab} />

        <div className="mt-4 flex-1 space-y-6 sm:space-y-7 min-[900px]:mt-6">
          {activeTab === "home" && (
            <HomePanel
              data={data}
              accessState={accessState}
              doorCodeVisibility={doorCodeVisibility}
              doorCodePolicyText={data.doorCodePolicyText}
              showUnlockPrompt={showUnlockPrompt}
              onOpenUnlock={onOpenUnlock}
              onExplore={() => setActiveTab("explore")}
              onSelectPlace={setSelectedPlace}
            />
          )}

          {activeTab === "explore" && (
            <ExplorePanel
              places={data.places}
              highlights={highlightedPlaces}
              selectedFilter={selectedFilter}
              onFilterChange={setSelectedFilter}
              onSelectPlace={setSelectedPlace}
            />
          )}

          {activeTab === "guide" && <GuidePanel data={data} />}
        </div>
      </div>

      <MobileNav activeTab={activeTab} onChange={setActiveTab} />

      <PlaceDetailSheet
        place={selectedPlace}
        open={Boolean(selectedPlace)}
        onOpenChange={(open) => {
          if (!open) setSelectedPlace(null)
        }}
        isDesktop={isDesktop}
      />
    </div>
  )
}

function buildHighlightCards(data: GuidebookData): HighlightCardData[] {
  if (!Array.isArray(data.highlights) || data.highlights.length === 0) return []
  const placeMap = new Map(data.places.map((place) => [place.id, place]))
  return data.highlights
    .map((highlight) => {
      const place = placeMap.get(highlight.place_id)
      if (!place) return null
      return {
        slot: highlight.slot,
        label: highlight.label,
        motivation: highlight.motivation,
        place,
      }
    })
    .filter(Boolean) as HighlightCardData[]
}

function buildTimelineItems(data: GuidebookData) {
  const items: Array<{ icon: LucideIcon; label: string; value: string; note?: string }> = []

  if (data.strategy?.check_in) {
    items.push({ icon: CalendarDays, label: "Arrival", value: formatDate(data.strategy.check_in) })
  }

  if (data.property.checkin_time) {
    items.push({ icon: Clock3, label: "Check-in", value: data.property.checkin_time })
  }

  if (data.houseRules?.checkout?.checkoutTime) {
    items.push({ icon: Clock3, label: "Check-out", value: data.houseRules.checkout.checkoutTime })
  }

  if (data.strategy?.check_out) {
    items.push({ icon: CalendarDays, label: "Departure", value: formatDate(data.strategy.check_out) })
  }

  return items
}

function buildPropertyInfo(data: GuidebookData): PropertyInfoItem[] {
  const items: PropertyInfoItem[] = []

  if (data.property.parking_instruction) {
    items.push({ key: "parking", label: "Parking", value: data.property.parking_instruction })
  }

  if (data.property.trash_instruction) {
    items.push({ key: "waste", label: "Waste & recycling", value: data.property.trash_instruction })
  }

  if (data.property.supermarket_hint) {
    items.push({ key: "supermarket", label: "Groceries nearby", value: data.property.supermarket_hint })
  }

  return items
}

function buildRuleGroups(rules: GuidebookHouseRules, property: GuidebookData["property"]): RuleGroup[] {
  const groups: RuleGroup[] = []

  if (showInGuidebook(rules.quietHours)) {
    const items = [
      rules.quietHours?.startTime && rules.quietHours?.endTime
        ? `${rules.quietHours.startTime}–${rules.quietHours.endTime}`
        : null,
      rules.quietHours?.strictness === "strict" ? "Please keep noise especially low during these hours." : null,
      rules.quietHours?.delivery,
    ].filter(Boolean) as string[]
    groups.push({ key: "quietHours", title: "Quiet hours", items: items.length > 0 ? items : ["Noise should be kept to a respectful level."] })
  }

  if (showInGuidebook(rules.smoking)) {
    groups.push({
      key: "smoking",
      title: "Smoking",
      items: [friendlyValue(rules.smoking?.value), rules.smoking?.notes, rules.smoking?.delivery].filter(Boolean) as string[],
    })
  }

  if (showInGuidebook(rules.pets)) {
    groups.push({
      key: "pets",
      title: "Pets",
      items: [friendlyValue(rules.pets?.value), rules.pets?.notes, rules.pets?.delivery].filter(Boolean) as string[],
    })
  }

  if (showInGuidebook(rules.visitors)) {
    groups.push({
      key: "visitors",
      title: "Visitors",
      items: [friendlyValue(rules.visitors?.value), rules.visitors?.notes, rules.visitors?.delivery].filter(Boolean) as string[],
    })
  }

  if (showInGuidebook(rules.parties)) {
    groups.push({
      key: "parties",
      title: "Parties & events",
      items: [friendlyValue(rules.parties?.value), rules.parties?.notes, rules.parties?.delivery].filter(Boolean) as string[],
    })
  }

  if (rules.waste?.enabled) {
    const items = [
      rules.waste.binLocation ? `Bins: ${rules.waste.binLocation}` : null,
      rules.waste.recyclingTypes?.length ? `Recycle: ${rules.waste.recyclingTypes.join(", ")}` : null,
      rules.waste.notes,
      property.trash_instruction && property.trash_instruction !== rules.waste.notes ? property.trash_instruction : null,
    ].filter(Boolean) as string[]
    if (items.length > 0) groups.push({ key: "waste", title: "Waste & recycling", items })
  }

  if (rules.safety?.enabled) {
    const items = [
      ...(rules.safety.enabledRules || []).map(friendlySafety),
      ...(rules.safety.customRules || []),
    ]
    if (items.length > 0) groups.push({ key: "safety", title: "Safety", items })
  }

  if ((!property.parking_instruction && rules.parking?.instruction) || rules.parking?.notes) {
    const items = [rules.parking?.instruction, rules.parking?.notes].filter(Boolean) as string[]
    if (items.length > 0) groups.push({ key: "parking", title: "Parking", items })
  }

  if (rules.outdoor?.rules?.length || rules.outdoor?.notes) {
    groups.push({
      key: "outdoor",
      title: "Outdoor areas",
      items: [...(rules.outdoor?.rules || []), ...(rules.outdoor?.notes ? [rules.outdoor.notes] : [])],
    })
  }

  if (rules.custom?.rules?.length) {
    groups.push({
      key: "custom",
      title: "Additional notes",
      items: rules.custom.rules,
    })
  }

  return groups.filter((group) => group.items.length > 0)
}

function deriveDoorCodeVisibility(data: GuidebookData): DoorCodeVisibility {
  const hint = data.doorCodeVisibility
  if (hint === "available_now" || hint === "available_on_arrival" || hint === "locked") return hint
  if (data.property.door_code_status === "pending") return "available_on_arrival"
  if (data.property.door_code_status === "visible") return "available_now"
  if (data.property.door_code_status === "not_configured" || !data.property.door_code) return "not_configured"
  return "locked"
}

function formatDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(date)
}

function formatTypeLabel(type?: string) {
  if (!type) return ""
  return type
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function showInGuidebook(rule: { enabled: boolean; surfaces?: { guidebook?: boolean } } | undefined) {
  return Boolean(rule?.enabled && rule.surfaces?.guidebook !== false)
}

function friendlyValue(value?: string) {
  switch (value) {
    case "not_allowed":
      return "Not permitted"
    case "allowed":
      return "Allowed"
    case "allowed_outside":
      return "Allowed outside only"
    case "approval_required":
      return "Please ask your host first"
    case "designated_areas":
      return "Designated areas only"
    default:
      return value ? formatTypeLabel(value) : "Please check with your host"
  }
}

function friendlySafety(value: string) {
  switch (value) {
    case "fire_extinguisher":
      return "Fire extinguisher available"
    case "first_aid_kit":
      return "First aid kit available"
    case "smoke_detector":
      return "Smoke detector installed"
    case "carbon_monoxide_detector":
      return "Carbon monoxide detector installed"
    case "security_camera_exterior":
      return "Exterior security camera in place"
    default:
      return formatTypeLabel(value)
  }
}

function getDoorCodeLabel(
  data: GuidebookData,
  accessState: AccessState,
  visibility: DoorCodeVisibility,
  _visible: boolean,
) {
  if (accessState === "authenticated" && visibility === "available_now" && data.property.door_code) {
    return "Ready now"
  }

  if (visibility === "available_on_arrival") {
    return data.property.door_code_available_date || "Available at check-in"
  }

  if (visibility === "not_configured") {
    return "Not configured"
  }

  return "Protected"
}

function getDoorCodeHint(accessState: AccessState, visibility: DoorCodeVisibility, policyText?: string) {
  if (accessState === "authenticated" && visibility === "available_now") return undefined
  if (visibility === "available_on_arrival") return policyText || "This code becomes visible at check-in."
  if (visibility === "not_configured") return "No door code is configured for this property."
  return policyText || "Door access is revealed only after booking verification."
}

function getHeroSummary(data: GuidebookData) {
  if (data.weather?.temperature_c != null && data.weather?.weather_label) {
    return `${Math.round(data.weather.temperature_c)}°C · ${data.weather.weather_label}`
  }

  if (data.strategy?.nights) {
    return `${data.strategy.nights} night${data.strategy.nights === 1 ? "" : "s"}`
  }

  return null
}
